export default class ClipPathAnimator {
  constructor() {
    this.elements = document.querySelectorAll('.gr-marco-clip.clip-on-scroll');
    if (!this.elements.length) return;

    // Almacenar el estado de cada elemento
    this.elementStates = new WeakMap();
    
    // Inicializar
    this.lastScrollY = window.scrollY;
    this.scrollDirection = 'down';
    this.raf = null;
    
    // Vincular métodos
    this.animate = this.animate.bind(this);
    this.onScroll = this.onScroll.bind(this);
    
    // Calcular valores iniciales
    this.calculateGlobalValues();
    
    // Configurar cada elemento
    this.elements.forEach(el => {
      this.elementStates.set(el, {
        clipValue: this.maxClipPercent,
        animationProgress: 0
      });
      
      // Aplicar el clip inicial
      this.updateElementClip(el, this.maxClipPercent);
    });

    // Iniciar escucha de eventos
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', () => {
      this.calculateGlobalValues();
      this.onScroll();
    });
    
    // Primera animación
    this.startAnimation();
  }
  
  calculateGlobalValues() {
    // Obtener el ancho máximo desde la variable global CSS
    this.globalContentSize = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--wp--style--global--content-size') || '1400'
    );
    this.viewportWidth = window.innerWidth;
    this.viewportHeight = window.innerHeight;
    
    // Calcular el valor de recorte inicial basado en el contenedor global
    const clipStart = Math.max((this.viewportWidth - this.globalContentSize) / 2, 0);
    this.maxClipPercent = (clipStart / this.viewportWidth) * 100;
    
    // Radio máximo para las esquinas redondeadas
    this.maxRadius = this.viewportWidth * 0.02;
  }

  onScroll() {
    const currentScroll = window.scrollY;
    this.scrollDirection = currentScroll > this.lastScrollY ? 'down' : 'up';
    this.lastScrollY = currentScroll;
  }

  // Función de easing para suavizar especialmente el final
  easeOutQuint(x) {
    return 1 - Math.pow(1 - x, 5);
  }
  
  // Función de easing para todo el rango, extra suave
  smoothStep(x) {
    // Función suavizada que es especialmente delicada en los extremos
    return x * x * (3 - 2 * x);
  }

  updateElementClip(element, clipValue) {
    // Asegurarnos de que clipValue está en el rango correcto y con varios decimales para transiciones más suaves
    clipValue = Math.max(0, Math.min(clipValue, this.maxClipPercent));
    
    // Radio proporcional al recorte con transición extra suave para el radio
    const radiusProgress = this.smoothStep(clipValue / this.maxClipPercent);
    const radius = this.maxRadius * radiusProgress;
    
    // Umbral extremadamente bajo para evitar problemas de renderizado
    if (clipValue < 0.001) {
      // Sin recorte cuando es prácticamente 0
      element.style.clipPath = 'none';
    } else {
      // Usamos toFixed(3) para asegurar suficiente precisión pero evitar problemas de rendimiento
      element.style.clipPath = `inset(0% ${clipValue.toFixed(3)}% 0% ${clipValue.toFixed(3)}% round ${radius.toFixed(3)}px)`;
    }
  }
  
  startAnimation() {
    if (!this.raf) {
      this.raf = requestAnimationFrame(this.animate);
    }
  }

  animate(timestamp) {
    const vh = this.viewportHeight;
    const scrollTriggerTop = vh * 0.8;     // Comienza al 10% visible desde arriba
    const scrollTriggerBottom = vh * 0.2;  // Completo al 50% de la pantalla
    
    // Rango de animación (en píxeles)
    const animationRange = scrollTriggerTop - scrollTriggerBottom;
    
    this.elements.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const state = this.elementStates.get(el);
      
      // Calculamos la posición del elemento
      const elementTop = rect.top;
      
      // Calculamos un valor de progreso base (lineal)
      let rawProgress;
      
      if (this.scrollDirection === 'down') {
        if (elementTop <= scrollTriggerBottom) {
          rawProgress = 1;
        } else if (elementTop >= scrollTriggerTop) {
          rawProgress = 0;
        } else {
          rawProgress = (scrollTriggerTop - elementTop) / animationRange;
        }
      } else { // scroll up
        const upTriggerBottom = vh * 0.25; // Ligeramente más responsivo

        
        if (elementTop >= scrollTriggerTop) {
          rawProgress = 0;
        } else if (elementTop <= upTriggerBottom) {
          rawProgress = 1;
        } else {
          rawProgress = 1 - ((elementTop - upTriggerBottom) / (scrollTriggerTop - upTriggerBottom));
        }
      }
      
      // Limitamos el valor entre 0 y 1
      rawProgress = Math.max(0, Math.min(1, rawProgress));
      
      // Aplicamos una función de easing para suavizar la curva de animación, especialmente al final
      const easedProgress = this.easeOutQuint(rawProgress);
      
      // Para una transición ultra suave, usamos un factor de easing muy pequeño
      // Esto hace que los cambios sean más graduales y menos perceptibles
      const ultraSmoothFactor = 0.05; 
      
      // Actualizamos el progreso de manera muy gradual
      state.animationProgress += (easedProgress - state.animationProgress) * ultraSmoothFactor;
      
      // Calcular el valor de recorte basado en el progreso con la función smoothStep para extra suavidad
      // Esto es especialmente importante para los valores cercanos a 0 (sin recorte)
      const smoothProgress = this.smoothStep(state.animationProgress);
      const clipValue = this.maxClipPercent * (1 - smoothProgress);
      
      // Aplicar el clip actualizado
      this.updateElementClip(el, clipValue);
    });

    // Mantener la animación activa continuamente
    this.raf = requestAnimationFrame(this.animate);
  }
}